import React, { useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { useParams, useSearchParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { formatDateSafe } from '../utils/dateFormat';

import { imageBuildsApi } from '../services/imageBuildsService';
import { ImageBuildDoc, ImageBuildStatus, ImageUsageResult, RunUsageStats } from '../services/types';
import { useApiQuery } from '../hooks/useApiQuery';
import { useAuth } from '../contexts/AuthContext';
import { NC_972_WIZARD_ENABLED } from '../config/featureFlags';
import { TableSkeleton } from '../components/SkeletonLoaders';
import EmptyState from '../components/EmptyState';
import ErrorMessage from '../components/ErrorMessage';

const REFETCH_INTERVAL_MS = 30_000;

const STATUS_COLOR: Record<ImageBuildStatus, 'default' | 'info' | 'warning' | 'success' | 'error'> = {
  queued: 'info',
  running: 'warning',
  completed: 'success',
  failed: 'error',
  cancelled: 'default',
};

const STATUS_LABEL: Record<ImageBuildStatus, string> = {
  queued: 'Wartet',
  running: 'Läuft',
  completed: 'Fertig',
  failed: 'Fehler',
  cancelled: 'Abgebrochen',
};

/**
 * NC-1295: how often a version ran and how stable it was.
 *
 * Gallery `latest` is not a health signal (Architektur-Regel 16), so the columns
 * come from run documents via /api/image-usage. Absent usage for a version means
 * "no run used it", which is a real 0 — but an unreadable source means "unknown",
 * and the two must not look the same.
 */
const formatLastUse = (iso: string | null): string =>
  iso ? formatDateSafe(iso, 'dd.MM.yyyy') : '—';

const formatErrorRate = (rate: number | null): string =>
  rate === null ? '—' : `${Math.round(rate * 100)} %`;


function formatDuration(seconds: number | undefined): string {
  if (seconds === undefined || seconds === null) return '—';
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  if (minutes < 60) return rest === 0 ? `${minutes}min` : `${minutes}min ${rest}s`;
  const hours = Math.floor(minutes / 60);
  const mRest = minutes % 60;
  return `${hours}h ${mRest}min`;
}

function formatRequestedAt(iso: string): string {
  // formatDateSafe guards parseISO(undefined); keep the prior raw-string
  // fallback for non-empty-but-unparseable values (NC-939).
  const formatted = formatDateSafe(iso, 'dd.MM.yyyy HH:mm');
  return formatted === '—' && iso ? iso : formatted;
}

export default function ImageBuildsPage() {
  const routeParams = useParams<{ workloadId?: string }>();
  const [searchParams] = useSearchParams();
  const workloadId = routeParams.workloadId || searchParams.get('workloadId') || undefined;
  const { hasRole } = useAuth();
  const navigate = useNavigate();

  const { data, loading, error, refetch } = useApiQuery<ImageBuildDoc[]>(
    () => imageBuildsApi.list(workloadId ? { workloadId } : undefined),
    [workloadId]
  );

  // Background refetch every 30s. Skip while initial fetch is in flight so we
  // don't pile up duplicate requests.
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;
  useEffect(() => {
    const id = setInterval(() => {
      refetchRef.current();
    }, REFETCH_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  const builds = useMemo(() => data ?? [], [data]);

  // NC-1295: fetched separately and fault-isolated on purpose — the build history
  // is worth showing even when the usage aggregation is unavailable, so a failure
  // here must not blank the table. `usageError` is deliberately not rendered as a
  // page-level error for the same reason.
  const { data: usageData } = useApiQuery<ImageUsageResult>(
    () => imageBuildsApi.usage(workloadId ? { workloadId } : undefined),
    [workloadId]
  );

  const usageByVersion = useMemo(() => {
    const map = new Map<string, RunUsageStats>();
    for (const entry of usageData?.byVersion ?? []) {
      // Keyed by workload AND version: version strings are date-derived, so two
      // image definitions built in the same hour can share one.
      map.set(`${entry.workloadId}::${entry.version}`, entry.usage);
    }
    return map;
  }, [usageData]);

  const usageAvailable = usageData?.unknown.available ?? false;
  const unattributedRuns = usageData?.unknown.runCount ?? 0;

  const title = workloadId ? `Image-Builds — ${workloadId}` : 'Image-Builds';

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h4">{title}</Typography>
        <Box display="flex" alignItems="center" gap={1}>
          {/* NC-972: guided-wizard entry, gated by the flag (route only exists
              when the flag is on) + operator role. Invisible until go-live. */}
          {NC_972_WIZARD_ENABLED && hasRole('operator') && (
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => navigate('/images/new')}
            >
              Neues Image
            </Button>
          )}
          <Tooltip title="Aktualisieren">
            <IconButton onClick={refetch} aria-label="refresh">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <ErrorMessage
          message={error}
          title="Fehler beim Laden der Image-Builds"
          onRetry={refetch}
        />
      )}

      <Card>
        {loading && builds.length === 0 ? (
          <TableSkeleton
            rows={5}
            columns={6}
            headers={['Build', 'Workload', 'Status', 'Angefordert von', 'Angefordert', 'Aktion']}
          />
        ) : builds.length === 0 ? (
          <EmptyState
            variant="no-results"
            title="Keine Image-Builds"
            description={
              workloadId
                ? `Für Workload "${workloadId}" wurden noch keine Builds protokolliert.`
                : 'Es wurden noch keine Image-Builds protokolliert. Sobald Phase 3 dispatchen kann, erscheinen die Einträge hier.'
            }
          />
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Build</TableCell>
                  <TableCell>Workload</TableCell>
                  <TableCell>Tier / Umgebung</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Dauer</TableCell>
                  <TableCell>Image-Version</TableCell>
                  <TableCell align="right">Läufe</TableCell>
                  <TableCell>Letzter Einsatz</TableCell>
                  <TableCell align="right">Fehlerquote</TableCell>
                  <TableCell>Angefordert von</TableCell>
                  <TableCell>Angefordert</TableCell>
                  <TableCell align="center">GH-Run</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {builds.map((build) => {
                  const failureNote =
                    build.status === 'failed' && build.errorSummary
                      ? build.errorSummary
                      : undefined;
                  return (
                    <TableRow key={build.id} hover>
                      <TableCell>
                        {/* Image Builder 2.1 A6: the row's way into the detail page,
                            where the status also gets refreshed. */}
                        <RouterLink
                          to={`/images/${build.id}?workloadId=${encodeURIComponent(build.workloadId)}`}
                          style={{ color: 'inherit' }}
                        >
                          <Typography variant="body2" fontFamily="monospace">
                            {build.id.slice(0, 8)}
                          </Typography>
                        </RouterLink>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={build.workloadId.toUpperCase()}
                          size="small"
                          color="primary"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" component="div">
                          {build.tier}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" component="div">
                          {build.environment}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
                          <Tooltip title={failureNote ?? ''}>
                            <Chip
                              label={STATUS_LABEL[build.status]}
                              color={STATUS_COLOR[build.status]}
                              size="small"
                            />
                          </Tooltip>
                          {build.dryRun && (
                            // NC-985 (F3): distinguish a validate-only run from a
                            // real, promotable build in the list.
                            <Tooltip title="Validate-only — kein promotbares Image erzeugt">
                              <Chip label="Dry-Run" color="warning" size="small" variant="outlined" />
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>{formatDuration(build.durationSeconds)}</TableCell>
                      <TableCell>
                        {build.galleryImageVersion ? (
                          <Typography variant="body2" fontFamily="monospace">
                            {build.galleryImageVersion}
                          </Typography>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      {(() => {
                        const usage = build.galleryImageVersion
                          ? usageByVersion.get(`${build.workloadId}::${build.galleryImageVersion}`)
                          : undefined;
                        // Three distinct states, never collapsed: no version on the
                        // build (failed / dry-run) and an unreadable source both read
                        // '—', while a version nothing ran on is a real 0.
                        const unknown = !build.galleryImageVersion || !usageAvailable;
                        return (
                          <>
                            <TableCell align="right">
                              {unknown ? '—' : (usage?.runCount ?? 0)}
                            </TableCell>
                            <TableCell>
                              {unknown ? '—' : formatLastUse(usage?.lastRunAtUtc ?? null)}
                            </TableCell>
                            <TableCell align="right">
                              {unknown ? '—' : formatErrorRate(usage?.provisioningErrorRate ?? null)}
                            </TableCell>
                          </>
                        );
                      })()}
                      <TableCell>
                        <Typography variant="body2">
                          {build.requestedByEmail || build.requestedBy}
                        </Typography>
                      </TableCell>
                      <TableCell>{formatRequestedAt(build.requestedAt)}</TableCell>
                      <TableCell align="center">
                        {build.jobUrl ? (
                          <a
                            href={build.jobUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={`GitHub-Run für Build ${build.id}`}
                          >
                            <OpenInNewIcon fontSize="small" />
                          </a>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/*
          NC-1295: runs that cannot be attributed to any image version — legacy docs
          without buildMetadata, and docs where 110 recorded the literal "unknown".
          Stated explicitly because it is the MAJORITY today (17 of 28 produktiv runs
          when the endpoint shipped): a version table that silently omitted them would
          show a third of the real activity and still look plausible.
        */}
        {unattributedRuns > 0 && (
          <Box sx={{ p: 2, pt: 0 }}>
            <Typography variant="caption" color="text.secondary">
              {unattributedRuns} Lauf/Läufe ohne zuordenbare Image-Version (kein
              buildMetadata am Run) — in den Spalten oben nicht enthalten.
            </Typography>
          </Box>
        )}
      </Card>
    </Box>
  );
}
